import { Check } from 'lucide-react'
import { cn } from '@/shared/utils/cn'
import { Badge } from '@/shared/components/ui/badge'
import { formatServicePrice } from '../utils/formatServicePrice'
import type { CatalogService } from '../hooks/usePackageCatalog'

interface ServiceCardProps {
  service: CatalogService
  mode: 'view' | 'select'
  selected?: boolean
  /** Ya contratado por el negocio: se muestra pero no se puede seleccionar. */
  disabled?: boolean
  onToggle?: () => void
}

export function ServiceCard({
  service,
  mode,
  selected = false,
  disabled = false,
  onToggle,
}: ServiceCardProps) {
  const Icon = service.icon
  const selectable = mode === 'select' && !disabled

  const monthly = service.monthlyPrice
  const annual = service.annualPrice
  const savings =
    monthly && annual && monthly * 12 > 0
      ? Math.round((1 - annual / (monthly * 12)) * 100)
      : 0

  const priceBlock = service.hasPrice ? (
    <div>
      <div className="flex items-baseline gap-1">
        <span className="text-xl font-bold text-ink">
          {formatServicePrice(monthly, service.currencyName)}
        </span>
        <span className="text-xs text-ink-subtle">/mes</span>
      </div>
      <div className="mt-1 flex flex-wrap items-center gap-2">
        <span className="text-[11px] text-ink-muted">
          o {formatServicePrice(annual, service.currencyName)}/año
        </span>
        {savings > 0 && (
          <span className="rounded-full bg-success-soft px-1.5 py-0.5 text-[10px] font-bold text-success">
            Ahorra {savings}%
          </span>
        )}
      </div>
    </div>
  ) : (
    <p className="text-xs font-medium text-ink-subtle">Precio a confirmar</p>
  )

  return (
    <div
      role={selectable ? 'button' : undefined}
      tabIndex={selectable ? 0 : undefined}
      aria-pressed={selectable ? selected : undefined}
      aria-disabled={disabled || undefined}
      onClick={selectable ? onToggle : undefined}
      onKeyDown={
        selectable
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                onToggle?.()
              }
            }
          : undefined
      }
      className={cn(
        'relative flex h-full flex-col gap-3 rounded-card border bg-panel p-4 transition-all hover:shadow-md',
        selectable && 'cursor-pointer hover:border-brand/60',
        selected ? 'border-brand ring-1 ring-brand' : 'border-line',
        disabled && 'opacity-60',
      )}
    >
      {mode === 'select' && (selected || disabled) && (
        <span
          className={cn(
            'absolute right-3 top-3 inline-flex h-5 w-5 items-center justify-center rounded-full text-white',
            disabled ? 'bg-ink-subtle' : 'bg-brand',
          )}
        >
          <Check className="h-3 w-3" />
        </span>
      )}

      <div className="flex items-start gap-3">
        <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-brand-soft text-brand">
          <Icon className="h-5 w-5" />
        </span>
        <div className="min-w-0">
          <h4 className="truncate text-sm font-semibold text-ink">{service.name}</h4>
          <Badge variant="accent" className="mt-1">
            {service.typeLabel}
          </Badge>
        </div>
      </div>

      {service.description && (
        <p className="line-clamp-2 text-xs text-ink-muted">{service.description}</p>
      )}

      <div className="mt-auto border-t border-line pt-3">{priceBlock}</div>

      {disabled && (
        <p className="text-[11px] font-medium text-ink-subtle">Ya contratado</p>
      )}
    </div>
  )
}
